import {
  Alert, App as AntApp, Button, Card, Checkbox, Collapse, Descriptions, Form, Input, InputNumber, Modal, Popconfirm,
  Select, Space, Switch, Table, Tag, Typography,
} from "antd";
import { useCallback, useState } from "react";
import { api, download, errorText } from "../api";
import { dateTime } from "../format";
import { canManage, useSession } from "../session";
import { useResource } from "../useResource";

/** `TenantPrivacy.Settings` */
export interface PrivacySettings {
  llmAllowed: boolean;
  legalApprovalVersion: string | null;
  approvedProviderId: string | null;
  pilotTrafficPercent: number;
  noticeVersion: number | null;
}

export interface PrivacyNotice {
  version: number;
  shortText: string;
  fullText: string;
  createdAt: string;
}

export interface PrivacyChange {
  id: string;
  tenantId: string;
  requested: PrivacySettings;
  status: "PENDING" | "APPROVED" | "REJECTED";
  requestedAt: string;
  decidedAt: string | null;
  note: string | null;
}

export interface PrivacyOverview {
  settings: PrivacySettings;
  notices: PrivacyNotice[];
  changes: PrivacyChange[];
}

export type RequestAction = "EXPORT" | "DELETE" | "RESTRICT" | "RECTIFY";

export interface PrivacyRequest {
  id: string;
  requestId: string;
  action: RequestAction;
  status: string;
  legalHold: boolean;
  reason: string;
  verifiedAt: string;
  requestedBy: string;
  requestedAt: string;
  approvedBy: string | null;
  completedAt: string | null;
  exportExpiresAt: string | null;
  errorCode: string | null;
}

const ACTIONS: Record<RequestAction, string> = {
  EXPORT: "Erişim (dışa aktarma)",
  DELETE: "Silme",
  RESTRICT: "Kısıtlama",
  RECTIFY: "Düzeltme",
};

const STATUS: Record<string, { label: string; color?: string }> = {
  PENDING_APPROVAL: { label: "Onay bekliyor", color: "gold" },
  APPROVED: { label: "Onaylandı, işleniyor", color: "blue" },
  EXPORT_READY: { label: "İndirilebilir", color: "green" },
  DOWNLOADED: { label: "İndirildi" },
  EXPIRED: { label: "Süresi doldu" },
  COMPLETED: { label: "Tamamlandı", color: "green" },
  MANUAL: { label: "Elle işlem", color: "purple" },
  REJECTED: { label: "Reddedildi" },
  FAILED: { label: "Başarısız", color: "red" },
};

/**
 * KVKK ayarları (docs/legal/). Mağaza veri sorumlusudur: aydınlatma metnini yayımlar, ilgili kişi başvurusunu
 * doğrular ve talimat verir. LLM ve pilot trafik değişikliği SalesOS platform operasyonunun onayıyla uygulanır;
 * LLM'i kapatmak her zaman anında yapılabilir.
 */
export function PrivacyPage() {
  const me = useSession((s) => s.me);
  const manage = canManage(me);
  const { data, error, reload } = useResource(useCallback(
    () => api.get<PrivacyOverview>("/api/console/privacy"), []));

  if (error) {
    return <Alert type="error" title={error} showIcon />;
  }
  if (!data) {
    return null;
  }
  return (
    <Space orientation="vertical" size="large" style={{ width: "100%" }}>
      <Typography.Title level={3}>Gizlilik ve KVKK</Typography.Title>
      <Typography.Paragraph type="secondary">
        Ziyaretçi verisinin veri sorumlusu mağazadır. Widget, yayındaki aydınlatma metni olmadan açılmaz; yapay zekâ
        yalnızca hukuk onayı ve onaylı sağlayıcıyla kullanılır.
      </Typography.Paragraph>
      <NoticeCard notices={data.notices} current={data.settings.noticeVersion} manage={manage} onChange={reload} />
      <GateCard overview={data} manage={manage} onChange={reload} />
      {manage && <RequestsCard />}
    </Space>
  );
}

function NoticeCard({ notices, current, manage, onChange }: {
  notices: PrivacyNotice[]; current: number | null; manage: boolean; onChange: () => void;
}) {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm<{ shortText: string; fullText: string }>();
  const [saving, setSaving] = useState(false);
  const published = notices.find((n) => n.version === current);

  async function publish(v: { shortText: string; fullText: string }) {
    setSaving(true);
    try {
      const { version } = await api.post<{ version: number }>("/api/console/privacy/notices", v);
      message.success(`Aydınlatma metni sürüm ${version} yayında`);
      form.resetFields();
      onChange();
    } catch (e) {
      message.error(errorText(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card title="Aydınlatma metni" extra={published ? <Tag color="green">Sürüm {published.version}</Tag> : null}>
      {published ? (
        <Space orientation="vertical" style={{ width: "100%" }}>
          <Typography.Paragraph style={{ marginBottom: 0 }}>{published.shortText}</Typography.Paragraph>
          <Collapse size="small" items={[{ key: "full", label: "Tam metin",
            children: <Typography.Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
              {published.fullText}</Typography.Paragraph> }]} />
        </Space>
      ) : (
        <Alert type="warning" showIcon title="Yayında aydınlatma metni yok; widget ziyaretçilere açılmaz." />
      )}
      {manage && (
        <Form form={form} layout="vertical" onFinish={publish} style={{ marginTop: 16 }}>
          <Form.Item label="Kısa katman (widget'ta ilk mesajdan önce)" name="shortText"
            rules={[{ required: true, whitespace: true, max: 600 }]}>
            <Input.TextArea autoSize={{ minRows: 2 }} showCount maxLength={600} />
          </Form.Item>
          <Form.Item label="Tam metin" name="fullText" rules={[{ required: true, whitespace: true, max: 20000 }]}
            tooltip="Şablondaki [ALAN]'lar doldurulmadan yayımlanamaz">
            <Input.TextArea autoSize={{ minRows: 6 }} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={saving}>Yeni sürümü yayımla</Button>
        </Form>
      )}
    </Card>
  );
}

function GateCard({ overview, manage, onChange }: { overview: PrivacyOverview; manage: boolean; onChange: () => void }) {
  const { message } = AntApp.useApp();
  const [open, setOpen] = useState(false);
  const s = overview.settings;
  const pending = overview.changes.find((c) => c.status === "PENDING");

  async function stop() {
    try {
      await api.post("/api/console/privacy/llm-stop");
      message.success("Yapay zekâ kapatıldı; asistan kural tabanlı çalışıyor");
      onChange();
    } catch (e) {
      message.error(errorText(e));
    }
  }

  async function request(v: PrivacySettings) {
    try {
      await api.post("/api/console/privacy/changes", v);
      message.success("Değişiklik talebi platform onayına gönderildi");
      setOpen(false);
      onChange();
    } catch (e) {
      message.error(errorText(e));
    }
  }

  return (
    <Card title="Yapay zekâ ve pilot trafiği" extra={manage && (
      <Space>
        <Button disabled={Boolean(pending)} onClick={() => setOpen(true)}>Değişiklik iste</Button>
        <Popconfirm title="Yapay zekâ hemen kapatılsın mı?" description="Asistan kural tabanlı akışa döner."
          okText="Kapat" cancelText="Vazgeç" onConfirm={stop}>
          <Button danger disabled={!s.llmAllowed}>Yapay zekâyı kapat</Button>
        </Popconfirm>
      </Space>
    )}>
      <Descriptions column={1} size="small" items={[
        { key: "llm", label: "Yapay zekâ", children: s.llmAllowed ? <Tag color="green">İzinli</Tag> : <Tag>Kapalı</Tag> },
        { key: "legal", label: "Hukuk onay sürümü", children: s.legalApprovalVersion ?? "—" },
        { key: "provider", label: "Onaylı sağlayıcı", children: s.approvedProviderId ?? "—" },
        { key: "pilot", label: "Pilot trafiği", children: `%${s.pilotTrafficPercent}` },
      ]} />
      {pending && (
        <Alert type="info" showIcon style={{ marginTop: 12 }}
          title={`Platform onayı bekleyen talep: yapay zekâ ${pending.requested.llmAllowed ? "açık" : "kapalı"}, `
            + `pilot %${pending.requested.pilotTrafficPercent} (${dateTime(pending.requestedAt)})`} />
      )}
      {open && (
        <Modal open title="Değişiklik talebi" okText="Talebi gönder" cancelText="Vazgeç" footer={null}
          onCancel={() => setOpen(false)} destroyOnHidden>
          <Form<PrivacySettings> layout="vertical" initialValues={s} onFinish={request}>
            <Form.Item label="Yapay zekâ izni" name="llmAllowed" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item label="Hukuk kabul formu sürümü" name="legalApprovalVersion" rules={[{ max: 64 }]}
              tooltip="İmzalı pilot hukuk kabul formunun sürümü (docs/legal/pilot-hukuk-kabul-formu.md)">
              <Input />
            </Form.Item>
            <Form.Item label="Onaylı LLM sağlayıcısı" name="approvedProviderId" rules={[{ max: 48 }]}>
              <Input placeholder="anthropic" />
            </Form.Item>
            <Form.Item label="Pilot trafiği (%)" name="pilotTrafficPercent" rules={[{ required: true }]}>
              <InputNumber min={0} max={100} />
            </Form.Item>
            <Button type="primary" htmlType="submit">Talebi gönder</Button>
          </Form>
        </Modal>
      )}
    </Card>
  );
}

interface RequestDraft {
  requestId: string;
  action: RequestAction;
  verifiedAt: string;
  ids: string;
  idType: "sessionIds" | "conversationIds" | "attributionIds" | "orderRefs";
  legalHold: boolean;
  reason: string;
}

function RequestsCard() {
  const { message } = AntApp.useApp();
  const { data, error, reload } = useResource(useCallback(
    () => api.get<{ requests: PrivacyRequest[] }>("/api/console/privacy/requests"), []));
  const [open, setOpen] = useState(false);

  async function create(v: RequestDraft) {
    const ids = v.ids.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
    try {
      await api.post("/api/console/privacy/requests", {
        requestId: v.requestId.trim(), action: v.action, verifiedAt: new Date(v.verifiedAt).toISOString(),
        selectors: { [v.idType]: ids }, legalHold: v.legalHold, reason: v.reason.trim(),
      });
      message.success("Talimat kaydedildi; başka bir yöneticinin onayı bekleniyor");
      setOpen(false);
      reload();
    } catch (e) {
      message.error(errorText(e));
    }
  }

  async function decide(r: PrivacyRequest, approve: boolean) {
    try {
      await api.post(`/api/console/privacy/requests/${r.id}/${approve ? "approve" : "reject"}`);
      message.success(approve ? "Onaylandı" : "Reddedildi");
      reload();
    } catch (e) {
      message.error(errorText(e));
    }
  }

  async function save(r: PrivacyRequest) {
    try {
      const blob = await download(`/api/console/privacy/requests/${r.id}/export`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kisisel-veri-${r.requestId}.json`;
      a.click();
      URL.revokeObjectURL(url);
      reload();
    } catch (e) {
      message.error(errorText(e));
    }
  }

  return (
    <Card title="İlgili kişi talepleri" extra={<Button type="primary" onClick={() => setOpen(true)}>Yeni talimat</Button>}>
      <Typography.Paragraph type="secondary">
        Başvuruyu mağaza doğrular; ziyaretçinin bildiği bir kimlik tek başına doğrulama değildir. Talimatı başka bir
        mağaza yöneticisi onaylar. Silme ayrı bakım sürecinde uygulanır; dışa aktarma bir kez ve 72 saat içinde
        indirilebilir.
      </Typography.Paragraph>
      {error && <Alert type="error" title={error} showIcon />}
      <Table<PrivacyRequest> rowKey="id" dataSource={data?.requests ?? []} pagination={false} size="small"
        scroll={{ x: 900 }} locale={{ emptyText: "Henüz talep yok" }}
        columns={[
          { title: "Başvuru", dataIndex: "requestId", width: 140 },
          { title: "Talep", dataIndex: "action", width: 160, render: (a: RequestAction) => ACTIONS[a] },
          { title: "Durum", dataIndex: "status", width: 170,
            render: (s: string, r) => <Space size={4}><Tag color={STATUS[s]?.color}>{STATUS[s]?.label ?? s}</Tag>
              {r.errorCode === "LEGAL_HOLD" && <Tag>Hukuki saklama</Tag>}</Space> },
          { title: "Kaydedilme", dataIndex: "requestedAt", width: 150, render: dateTime },
          { title: "Gerekçe", dataIndex: "reason" },
          { title: "", key: "actions", width: 190, render: (_: unknown, r: PrivacyRequest) => (
            <Space>
              {r.status === "PENDING_APPROVAL" && (
                <>
                  <Popconfirm title="Talimat onaylansın mı?" okText="Onayla" cancelText="Vazgeç"
                    onConfirm={() => decide(r, true)}>
                    <Button size="small" type="primary">Onayla</Button>
                  </Popconfirm>
                  <Button size="small" onClick={() => decide(r, false)}>Reddet</Button>
                </>
              )}
              {r.status === "EXPORT_READY" && <Button size="small" onClick={() => save(r)}>İndir</Button>}
            </Space>
          ) },
        ]} />
      {open && (
        <Modal open title="Yeni ilgili kişi talimatı" footer={null} onCancel={() => setOpen(false)} destroyOnHidden>
          <Form<RequestDraft> layout="vertical" onFinish={create}
            initialValues={{ action: "EXPORT", idType: "attributionIds", legalHold: false }}>
            <Form.Item label="Mağaza başvuru kimliği" name="requestId" rules={[{ required: true, max: 100 }]}>
              <Input placeholder="KVKK-2026-0042" />
            </Form.Item>
            <Form.Item label="Talep" name="action" rules={[{ required: true }]}>
              <Select options={Object.entries(ACTIONS).map(([value, label]) => ({ value, label }))} />
            </Form.Item>
            <Form.Item label="Doğrulama zamanı" name="verifiedAt" rules={[{ required: true }]}>
              <Input type="datetime-local" />
            </Form.Item>
            <Form.Item label="Kimlik türü" name="idType">
              <Select options={[
                { value: "attributionIds", label: "Ziyaretçi (attribution) kimliği" },
                { value: "sessionIds", label: "Oturum kimliği" },
                { value: "conversationIds", label: "Konuşma kimliği" },
                { value: "orderRefs", label: "Sipariş referansı" },
              ]} />
            </Form.Item>
            <Form.Item label="Kimlikler" name="ids" rules={[{ required: true }]}
              tooltip="Virgül veya satır sonuyla ayırın; en fazla 100">
              <Input.TextArea autoSize={{ minRows: 2 }} />
            </Form.Item>
            <Form.Item name="legalHold" valuePropName="checked">
              <Checkbox>Hukuki saklama yükümlülüğü var (silme yerine elle değerlendirilir)</Checkbox>
            </Form.Item>
            <Form.Item label="Gerekçe ve doğrulama yöntemi" name="reason" rules={[{ required: true, max: 500 }]}>
              <Input.TextArea autoSize={{ minRows: 2 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit">Kaydet</Button>
          </Form>
        </Modal>
      )}
    </Card>
  );
}

/** Platform operasyonu: mağazaların LLM ve pilot değişikliği taleplerini pilot hukuk kabul formuna göre onaylar. */
export function PrivacyApprovalsPage() {
  const { message } = AntApp.useApp();
  const { data, error, reload } = useResource(useCallback(
    () => api.get<{ changes: PrivacyChange[] }>("/api/console/platform/privacy-changes"), []));

  async function decide(c: PrivacyChange, approve: boolean) {
    try {
      await api.post(`/api/console/platform/tenants/${c.tenantId}/privacy-changes/${c.id}/${approve ? "approve" : "reject"}`);
      message.success(approve ? "Onaylandı" : "Reddedildi");
      reload();
    } catch (e) {
      message.error(errorText(e));
    }
  }

  if (error) {
    return <Alert type="error" title={error} showIcon />;
  }
  return (
    <Card title="Gizlilik onayları">
      <Typography.Paragraph type="secondary">
        Yapay zekâ iznini yalnızca imzalı pilot hukuk kabul formu ve onaylı sağlayıcı kaydıyla onaylayın
        (docs/legal/pilot-hukuk-kabul-formu.md).
      </Typography.Paragraph>
      <Table<PrivacyChange> rowKey="id" dataSource={data?.changes ?? []} pagination={false} size="small"
        scroll={{ x: 800 }} locale={{ emptyText: "Bekleyen talep yok" }}
        columns={[
          { title: "Mağaza", dataIndex: "tenantId", width: 140 },
          { title: "Yapay zekâ", key: "llm", width: 110,
            render: (_: unknown, c) => (c.requested.llmAllowed ? <Tag color="green">Açık</Tag> : <Tag>Kapalı</Tag>) },
          { title: "Hukuk onayı", key: "legal", render: (_: unknown, c) => c.requested.legalApprovalVersion ?? "—" },
          { title: "Sağlayıcı", key: "provider", width: 110, render: (_: unknown, c) => c.requested.approvedProviderId ?? "—" },
          { title: "Pilot", key: "pilot", width: 80, render: (_: unknown, c) => `%${c.requested.pilotTrafficPercent}` },
          { title: "İstenme", dataIndex: "requestedAt", width: 150, render: dateTime },
          { title: "", key: "actions", width: 170, render: (_: unknown, c: PrivacyChange) => (
            <Space>
              <Popconfirm title="Talep onaylansın mı?" okText="Onayla" cancelText="Vazgeç" onConfirm={() => decide(c, true)}>
                <Button size="small" type="primary">Onayla</Button>
              </Popconfirm>
              <Button size="small" onClick={() => decide(c, false)}>Reddet</Button>
            </Space>
          ) },
        ]} />
    </Card>
  );
}
